// @ts-check
/**
 * ConversationReferencesView Component (Factory Pattern)
 *
 * Displays ReferenceUnitHandle for EmailCommunication-Conversation ReferenceUnit.
 */

/** @typedef {import('../../../../stores/referenceUnitStore').ReferenceUnitHandle} ReferenceUnitHandle */

/**
 * @param {any} deps - Injected Vue/store dependencies, see componentRegistry.js.
 *   Typed as `any` to preserve the factory-pattern escape hatch for
 *   dynamically-loaded configurable types.
 */
export default function createComponent(deps) {
    const {
        computed,
        inject,
        useDataViewStore,
        RawViewContainer,
        ReferenceRowTags,
        formatDate,
        useRowSelection,
    } = deps;

    return {
        name: 'ConversationReferencesView',

        components: {
            RawViewContainer,
            ReferenceRowTags,
        },

        props: {
            references: {
                type: Array,
                required: true,
            },
            question: {
                type: String,
                required: true,
            },
        },

        /** @param {{ references: ReferenceUnitHandle[], question: string }} props */
        setup(props) {
            const dataViewStore = useDataViewStore();
            const openDetailView = inject('openDetailView', null);

            /** @param {ReferenceUnitHandle} reference */
            const handleConversationClick = (reference) => {
                console.log('[ConversationReferencesView] Opening detail view for:', reference);

                const refIndex = sortedReferences.value.findIndex(
                    /** @param {ReferenceUnitHandle} r */
                    (r) => r.content_hash === reference.content_hash,
                );

                if (refIndex !== -1) {
                    dataViewStore.communicationGroups = sortedReferences.value;
                    dataViewStore.currentGroupIndex = refIndex;

                    if (openDetailView) {
                        openDetailView();
                    } else {
                        dataViewStore.openCommunicationRawView(sortedReferences.value, refIndex);
                    }
                }
            };

            /** @param {ReferenceUnitHandle} ref */
            const isRowSelected = (ref) => {
                const currentRef =
                    dataViewStore.communicationGroups[dataViewStore.currentGroupIndex];
                return currentRef && currentRef.content_hash === ref.content_hash;
            };

            const sortedReferences = computed(() => {
                /** @type {ReferenceUnitHandle[]} */
                const refs = [...props.references];
                refs.sort(
                    /**
                     * @param {ReferenceUnitHandle} a
                     * @param {ReferenceUnitHandle} b
                     */
                    (a, b) => {
                        const dateA = /** @type {string | undefined} */ (a.metadata?.start_date);
                        const dateB = /** @type {string | undefined} */ (b.metadata?.start_date);

                        if (!dateA && !dateB) {
                            return (
                                parseInt(a.display_identifier || '0') -
                                parseInt(b.display_identifier || '0')
                            );
                        }
                        if (!dateA) return 1;
                        if (!dateB) return -1;

                        const dateDiff = new Date(dateA).getTime() - new Date(dateB).getTime();

                        if (dateDiff === 0) {
                            return (
                                parseInt(a.display_identifier || '0') -
                                parseInt(b.display_identifier || '0')
                            );
                        }

                        return dateDiff;
                    },
                );
                return refs;
            });

            // Bulk-selection checkboxes + session-only tag display. Shared
            // behavior lives in useRowSelection / referenceSelectionStore.
            const rowSelection = useRowSelection(() => sortedReferences.value);

            return {
                sortedReferences,
                formatDate,
                handleConversationClick,
                isRowSelected,
                openDetailView,
                selAllChecked: rowSelection.allChecked,
                selSomeChecked: rowSelection.someChecked,
                toggleSelectAll: rowSelection.toggleAll,
                isRowChecked: rowSelection.isChecked,
                toggleRowChecked: rowSelection.toggleChecked,
                showCommunicationRawView: computed(() => dataViewStore.showCommunicationRawView),
                communicationGroups: computed(() => dataViewStore.communicationGroups),
                currentGroupIndex: computed(() => dataViewStore.currentGroupIndex),
                closeCommunicationRawView: () => dataViewStore.closeCommunicationRawView(),
            };
        },

        template: `
            <div class="conversation-references-view">
                <!-- Conversation Table -->
                <div class="crv-table-container">
                    <table class="crv-table">
                        <colgroup>
                            <col class="crv-select-col" />
                            <col />
                            <col style="width: 25%" />
                            <col style="width: 25%" />
                        </colgroup>
                        <thead>
                            <tr>
                                <th class="crv-select-cell" @click.stop>
                                    <input
                                        type="checkbox"
                                        class="crv-row-checkbox"
                                        title="Select all"
                                        :checked="selAllChecked"
                                        :indeterminate="selSomeChecked"
                                        @change="toggleSelectAll"
                                    />
                                </th>
                                <th>Conversation ID</th>
                                <th>Start Date</th>
                                <th>End Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="ref in sortedReferences"
                                :key="ref.content_hash"
                                :class="{ 'row-selected': isRowSelected(ref) }"
                                @click="handleConversationClick(ref)"
                            >
                                <td class="crv-select-cell" @click.stop>
                                    <input
                                        type="checkbox"
                                        class="crv-row-checkbox"
                                        :checked="isRowChecked(ref)"
                                        @change="toggleRowChecked(ref)"
                                    />
                                </td>
                                <td>
                                    <span class="crv-conversation-link">
                                        {{ ref.metadata?.document_identifier || ref.display_identifier }}
                                    </span>
                                    <ReferenceRowTags :content-hash="ref.content_hash" />
                                </td>
                                <td>
                                    {{ formatDate(ref.metadata.start_date) }}
                                </td>
                                <td>
                                    {{ formatDate(ref.metadata.end_date) }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Empty state -->
                <div v-if="sortedReferences.length === 0" class="crv-empty">
                    No conversation references found.
                </div>

                <!-- Raw View Modal (only if panel not available) -->
                <RawViewContainer
                    v-if="!openDetailView && communicationGroups.length > 0 && showCommunicationRawView"
                    :references="communicationGroups"
                    :initialIndex="currentGroupIndex"
                    :visible="showCommunicationRawView"
                    @close="closeCommunicationRawView"
                />
            </div>
        `,
    };
}
